import './PlanSelectionModal.less';

import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Radio, Space, Typography, Button } from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import { Product, ProductPrice } from '../../interfaces/product';
import {
  BASE_PRODUCTS,
  formatCurrency,
  getCreditsMonthly,
  PRODUCT_PRO_PLAN,
  PRODUCT_TEAM_PLAN,
} from '../PreviewInvoice/helpers';

const { Text, Title } = Typography;

interface PlanSelectionModalProps {
  open: boolean;
  onSelect: (priceId: string) => void;
  onCancel: () => void;
  loading?: boolean;
  title?: string;
}

type BillingInterval = 'month' | 'year';

/**
 * Get recurring prices from a product, sorted by interval (monthly first).
 * When multiple prices exist per interval, prefer the highest pricing_version
 * so the UI shows current prices while legacy prices stay active for existing subscribers.
 */
const getRecurringPrices = (product: Product): ProductPrice[] => {
  return product.prices
    .filter((p) => p.active && p.recurring)
    .sort((a, b) => {
      if (a.recurring?.interval === 'month' && b.recurring?.interval === 'year')
        return -1;
      if (a.recurring?.interval === 'year' && b.recurring?.interval === 'month')
        return 1;
      // Within the same interval, prefer the highest pricing_version
      const versionA = parseInt(a.metadata?.pricing_version || '0', 10);
      const versionB = parseInt(b.metadata?.pricing_version || '0', 10);
      if (versionA !== versionB) return versionB - versionA;
      return (a.unit_amount || 0) - (b.unit_amount || 0);
    });
};

/**
 * Get the display label for a billing interval.
 */
const getIntervalLabel = (interval: string): string => {
  switch (interval) {
    case 'month':
      return 'Monthly';
    case 'year':
      return 'Annual';
    default:
      return interval;
  }
};

const PlanSelectionModal: React.FC<PlanSelectionModalProps> = ({
  open,
  onSelect,
  onCancel,
  loading = false,
  title = 'Select your plan',
}) => {
  const { products } = useSelector((state: RootState) => state.products);
  const currentTeam = useSelector(
    (state: RootState) => state.team.currentTeam.record
  );
  // Derive team context from the team slice — state.access.isTeamContext is never populated
  const isTeamContext = !!currentTeam;
  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);

  // Reset selection when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedPriceId(null);
    }
  }, [open]);

  // Filter products to the correct type for this user context
  const availablePlans = useMemo(() => {
    const relevantProductId = isTeamContext
      ? PRODUCT_TEAM_PLAN
      : PRODUCT_PRO_PLAN;
    const basePlans = relevantProductId
      ? products.filter((p) => p.id === relevantProductId)
      : products.filter((p) => BASE_PRODUCTS.includes(p.id));

    return basePlans.map((product) => {
      const prices = getRecurringPrices(product);
      const pricesByInterval: Record<
        BillingInterval,
        ProductPrice | undefined
      > = { month: undefined, year: undefined };

      prices.forEach((price) => {
        const interval = price.recurring?.interval as BillingInterval;
        if (interval && !pricesByInterval[interval]) {
          pricesByInterval[interval] = price;
        }
      });

      // Calculate annual savings percentage
      let annualSavings: number | null = null;
      const monthly = pricesByInterval.month;
      const annual = pricesByInterval.year;
      if (monthly && annual) {
        const monthlyAnnualized = monthly.unit_amount * 12;
        const savings = Math.round(
          ((monthlyAnnualized - annual.unit_amount) / monthlyAnnualized) * 100
        );
        annualSavings = savings > 0 ? savings : null;
      }

      // Compute effective credits per interval (price metadata → product metadata).
      // Only show credits in the plan header when both intervals agree; otherwise
      // the header would contradict the per-price value shown after selection.
      const effectiveMonthlyCredits = getCreditsMonthly(
        pricesByInterval.month,
        product
      );
      const effectiveYearlyCredits = getCreditsMonthly(
        pricesByInterval.year,
        product
      );

      let creditsMonthly: number | undefined;
      if (pricesByInterval.month && pricesByInterval.year) {
        creditsMonthly =
          effectiveMonthlyCredits === effectiveYearlyCredits
            ? effectiveMonthlyCredits
            : undefined;
      } else if (pricesByInterval.month) {
        creditsMonthly = effectiveMonthlyCredits;
      } else if (pricesByInterval.year) {
        creditsMonthly = effectiveYearlyCredits;
      }

      return { product, pricesByInterval, annualSavings, creditsMonthly };
    });
  }, [products, isTeamContext]);

  if (availablePlans.length === 0) {
    return null;
  }

  const teamName = currentTeam?.cirra_organization_name;
  const modalTitle =
    isTeamContext && teamName ? `${title} for ${teamName}` : title;

  return (
    <Modal
      className="plan-selection-modal"
      title={modalTitle}
      open={open}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="next"
          type="primary"
          disabled={!selectedPriceId}
          loading={loading}
          onClick={() => selectedPriceId && onSelect(selectedPriceId)}
        >
          Continue
        </Button>,
      ]}
      width={520}
      destroyOnClose
    >
      <div className="plan-selection-content">
        <Text type="secondary" className="plan-selection-subtitle">
          Choose your plan and billing cycle
        </Text>

        <Radio.Group
          className="plan-options"
          value={selectedPriceId}
          onChange={(e) => setSelectedPriceId(e.target.value)}
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {availablePlans.map(
              ({
                product,
                pricesByInterval,
                annualSavings,
                creditsMonthly,
              }) => (
                <div key={product.id} className="plan-group">
                  <div className="plan-header">
                    <Title level={5} className="plan-name">
                      {product.name}
                    </Title>
                    {creditsMonthly && (
                      <Text type="secondary" className="plan-credits">
                        {creditsMonthly.toLocaleString()} credits/month
                      </Text>
                    )}
                  </div>

                  <Space
                    direction="vertical"
                    size="small"
                    style={{ width: '100%' }}
                  >
                    {(['month', 'year'] as BillingInterval[]).map(
                      (interval) => {
                        const price = pricesByInterval[interval];
                        if (!price) return null;

                        const monthlyEquivalent =
                          interval === 'year'
                            ? formatCurrency(
                                Math.round(price.unit_amount / 12),
                                price.currency
                              )
                            : null;

                        return (
                          <Radio
                            key={price.id}
                            value={price.id}
                            className="plan-option"
                          >
                            <div className="plan-option-content">
                              <div className="plan-option-header">
                                <span className="plan-option-interval">
                                  {getIntervalLabel(interval)}
                                </span>
                                {interval === 'year' && annualSavings && (
                                  <span className="plan-option-savings">
                                    Save {annualSavings}%
                                  </span>
                                )}
                              </div>
                              <div className="plan-option-price">
                                <span className="plan-option-amount">
                                  {formatCurrency(
                                    price.unit_amount,
                                    price.currency
                                  )}
                                </span>
                                <span className="plan-option-period">
                                  /{interval === 'year' ? 'year' : 'month'}
                                </span>
                                {monthlyEquivalent && (
                                  <span className="plan-option-equivalent">
                                    {' '}
                                    ({monthlyEquivalent}/mo)
                                  </span>
                                )}
                              </div>
                            </div>
                          </Radio>
                        );
                      }
                    )}
                  </Space>
                </div>
              )
            )}
          </Space>
        </Radio.Group>
      </div>
    </Modal>
  );
};

export default PlanSelectionModal;
